import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import { authService, userService, salaryStructureService } from '../../services/api';
import TopBar from '../../components/TopBar';

const fmt2 = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

export default function SalaryStructurePage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [staff,     setStaff]     = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading,   setLoading]   = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStaff,  setEditStaff]  = useState(null);
  const [form,       setForm]       = useState({ basicSalary: '', hRA: '', otherAllowances: '', fixedDeductions: '' });
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, sRes] = await Promise.all([
        userService.getAllUsers(),
        salaryStructureService.getAll(),
      ]);
      setStaff(uRes.data.filter(u => u.role !== 'Admin'));
      setStructures(sRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build a map: userId -> structure row
  const structMap = {};
  structures.forEach(s => { structMap[s.userId] = s; });

  const openEdit = (staffMember) => {
    const s = structMap[staffMember.id];
    setEditStaff(staffMember);
    setForm({
      basicSalary:      s?.basicSalary      ?? '',
      hRA:              s?.hRA              ?? '',
      otherAllowances:  s?.otherAllowances  ?? '',
      fixedDeductions:  s?.fixedDeductions  ?? '',
    });
    setDialogOpen(true);
  };

  const handleField = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await salaryStructureService.upsert({
        userId:          editStaff.id,
        basicSalary:     parseFloat(form.basicSalary)     || 0,
        hRA:             parseFloat(form.hRA)             || 0,
        otherAllowances: parseFloat(form.otherAllowances) || 0,
        fixedDeductions: parseFloat(form.fixedDeductions) || 0,
      });
      setDialogOpen(false);
      await load();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const gross = s =>
    (parseFloat(s?.basicSalary || 0) + parseFloat(s?.hRA || 0) + parseFloat(s?.otherAllowances || 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Salary Structure" onBack={() => navigate('/dashboard')} />

      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set monthly salary components for each staff member. Gross = Basic + HRA + Other Allowances.
          Net = Gross − Absence Deduction − Fixed Deductions.
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Staff Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Basic (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">HRA (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Other Allow. (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Gross (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Fixed Ded. (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.map((s, idx) => {
                    const st = structMap[s.id];
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</TableCell>
                        <TableCell>
                          <Chip label={s.role} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{st ? fmt2(st.basicSalary) : <Chip label="Not set" size="small" color="warning" />}</TableCell>
                        <TableCell align="right">{st ? fmt2(st.hRA) : '—'}</TableCell>
                        <TableCell align="right">{st ? fmt2(st.otherAllowances) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {st ? fmt2(gross(st)) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {st ? fmt2(st.fixedDeductions) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit fontSize="small" />}
                            onClick={() => openEdit(s)}
                          >
                            {st ? 'Edit' : 'Set'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {staff.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No staff members found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Salary Structure — {editStaff?.firstName} {editStaff?.lastName}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Basic Salary (₹)"
            type="number"
            size="small"
            value={form.basicSalary}
            onChange={handleField('basicSalary')}
            inputProps={{ min: 0, step: 100 }}
          />
          <TextField
            label="HRA (₹)"
            type="number"
            size="small"
            value={form.hRA}
            onChange={handleField('hRA')}
            inputProps={{ min: 0, step: 100 }}
          />
          <TextField
            label="Other Allowances (₹)"
            type="number"
            size="small"
            value={form.otherAllowances}
            onChange={handleField('otherAllowances')}
            inputProps={{ min: 0, step: 100 }}
          />
          <TextField
            label="Fixed Deductions / month (PF, TDS, etc.) (₹)"
            type="number"
            size="small"
            value={form.fixedDeductions}
            onChange={handleField('fixedDeductions')}
            inputProps={{ min: 0, step: 100 }}
          />
          <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Gross Salary: ₹ {fmt2(
                (parseFloat(form.basicSalary) || 0) +
                (parseFloat(form.hRA) || 0) +
                (parseFloat(form.otherAllowances) || 0)
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
